import { useState, useRef, useCallback } from "react";
import cn from "app/utils/classnames";
import { useInterval } from "app/utils/useInterval";
import { ONE_CHANCE_SETTINGS } from "app/data/game-settings";
import IconClose from "app/icons/IconClose";
import { checkIsRevealed, REVEALED_EMPTY_CHAR } from "./oneChanceSpellingUtils";

interface LetterBoxInteractiveProps {
  letter: string;
  showCaret: boolean;
  onRevealed: () => void;
  onTapped: () => void;
  allowReveal?: boolean;
  placeholderLetter?: string;
  heightClass?: string;
}

// A letter box that can be tapped to move the cursor or held to reveal its
// contents.
export default function LetterBoxInteractive({
  letter,
  showCaret,
  onTapped,
  onRevealed,
  allowReveal = true,
  placeholderLetter = "",
  heightClass = "min-h-12",
}: LetterBoxInteractiveProps) {
  const [isTouched, setIsTouched] = useState(false);
  const touchTimer = useRef(0);

  const revealed = checkIsRevealed(letter);
  const isRevealedEmpty = letter === REVEALED_EMPTY_CHAR;

  // Track how long the button has been touched
  const intervalDelay =
    revealed || (!isTouched && touchTimer.current <= 0)
      ? null
      : ONE_CHANCE_SETTINGS.letterBoxTimerDelta;

  useInterval(() => {
    // Track how long the box has been held, and trigger the reveal when it
    // reaches the max time.
    if (isTouched && allowReveal) {
      touchTimer.current += ONE_CHANCE_SETTINGS.letterBoxTimerDelta;
      touchTimer.current = Math.min(
        touchTimer.current,
        ONE_CHANCE_SETTINGS.letterBoxRevealTime,
      );
      if (touchTimer.current >= ONE_CHANCE_SETTINGS.letterBoxRevealTime) {
        onRevealed();
      }
    } else {
      touchTimer.current -= ONE_CHANCE_SETTINGS.letterBoxTimerDelta;
      touchTimer.current = Math.max(touchTimer.current, 0);
    }
  }, intervalDelay);

  const handlePointerDown = useCallback(() => {
    // If the letter cannot be revealed anyway, use pointer down to trigger the
    // tap event.
    if (!revealed && !allowReveal) {
      onTapped();
      return;
    }

    if (!revealed) {
      setIsTouched(true);
    }
  }, [allowReveal, revealed, onTapped]);

  const handlePointerUp = useCallback(() => {
    // When revealing is allowed, we need to treat pointer up as a tap event
    // rather than pointer down, since pointer down is what begins the reveal
    // animation.
    if (allowReveal && isTouched && !revealed) {
      onTapped();
    }

    setIsTouched(false);
  }, [isTouched, revealed, onTapped, allowReveal]);

  let bgColor = "bg-white";
  if (showCaret) {
    bgColor = "bg-white";
  } else if (revealed && !isRevealedEmpty) {
    bgColor = "bg-correct";
  } else if (letter) {
    bgColor = "bg-white";
  }

  return (
    <div className={cn("aspect-square")}>
      <div
        className={cn(
          "relative",
          "w-full h-full",
          heightClass,
          "outline outline-black",
          "overflow-hidden",
          "transition-all",
          bgColor,
          showCaret
            ? "animate-letter-box-up-next outline-gray-300 z-50"
            : "outline-black z-0",
          "flex justify-center items-center",
          !revealed && "cursor-pointer",
          "[webkit-touch-callout:none] select-none touch-none",
        )}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => {
          setIsTouched(false);
        }}
        onPointerOut={() => {
          setIsTouched(false);
        }}
      >
        <div className="relative flex justify-center">
          <div className="h-6.5 min-w-4">
            <p // THE ACTUAL LETTER
              className={cn(
                "w-full h-full",
                "font-mono text-xl text-center uppercase align-baseline select-none",
                placeholderLetter ? "text-gray-400" : "text-black",
                isRevealedEmpty ? "opacity-0" : "opacity-100",
              )}
            >
              {placeholderLetter || letter}
            </p>
            <p
              className={cn(
                "absolute",
                "top-0 bottom-0 right-0 left-0",
                showCaret ? "border-b" : "border-b-0",
              )}
            />
          </div>
          {isRevealedEmpty && (
            <IconClose
              className={"absolute"}
              strokeWidth={0.5}
              width={30}
              height={30}
            />
          )}
        </div>
        <div // OVERLAY SHOWN WHILE BOX IS TOUCHED
          className={cn(
            "absolute bottom-0 left-0 right-0 top-0",
            "rounded-full",
            "mask-origin-fill",
            "bg-black",
            "pointer-events-none",
            "duration-400 ease-out",
            revealed
              ? "scale-200 opacity-0"
              : isTouched
                ? "scale-200 bg-black opacity-25"
                : "scale-0 opacity-0",
          )}
        />
      </div>
    </div>
  );
}
